/**
 * Composite surfaces: the board dashboard, the quarterly CEO report (and its
 * PDF) and the AI data assistant in the support widget.
 *
 * Nothing here invents a KPI. Every figure is read from the wave-1 generators
 * the area pages use (clients.ts, people.ts, finance.ts), so /board, the staff
 * dashboard, the CEO report and the assistant agree for the same quarter.
 *
 *   boardDashboard(q)              GET /api/board/dashboard[?quarter=]
 *   ceoReport(q)                   GET /api/quarterly-report/:quarter (POST saves, returns the rebuilt report)
 *   ceoReportPdf(q)                GET /api/quarterly-report/:quarter/pdf (Blob, application/pdf)
 *   assistantReply(question, ...)  POST /api/chat/message
 */
import { MockContext, Route, respond } from '../router';
import { Quarter, allQuarters, currentQuarter, elapsed, iso, now, quarter, quarterParam, ymd } from '../clock';
import { getState } from '../state';
import { rng } from '../rng';
import { LEADERSHIP, ORG, activeClients } from '../world';
import {
    goalProgression,
    goalStats,
    incidentReportable,
    incidentStats,
    medicationStats,
    referrals,
    tendableStats,
    whsAuditSummary,
} from './clients';
import { complianceByCategory, complianceSummary, ehSummary, peopleSummary, trainingSummary, turnoverRate12m } from './people';
import { accountsReceivable, budgetYear, financeBoardTiles, financeYtd, fyLabel, occupancyStats, profitability } from './finance';
import { incidentsIn } from '../data/clients/incidents';
import { scopeOf } from '../data/clients/common';
import { MonthTotals, currentFy, fyToDateMonths, ledgerWages, monthFraction, monthTotals } from '../data/finance/ledger';

// ===================================================================== helpers

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** 1234567.8 → "1,234,568" (the backend's number_format($n, 0)). */
const num = (n: number): string => Math.round(n).toLocaleString('en-AU');

/** Dashboard-style money: $5.38M / $538k / $123. */
function money(n: number): string {
    const sign = n < 0 ? '-' : '';
    const a = Math.abs(n);
    if (a >= 1000000) {
        return `${sign}$${(a / 1000000).toFixed(2)}M`;
    }
    if (a >= 1000) {
        return `${sign}$${Math.round(a / 1000)}k`;
    }
    return `${sign}$${Math.round(a)}`;
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

function tally<T>(items: T[], key: (t: T) => string): [string, number][] {
    const m = new Map<string, number>();
    items.forEach(i => m.set(key(i), (m.get(key(i)) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function table(head: string[], rows: (string | number)[][]): string {
    return [`| ${head.join(' | ')} |`, `|${head.map((_, i) => (i === 0 ? '---' : '---:')).join('|')}|`, ...rows.map(r => `| ${r.join(' | ')} |`)].join('\n');
}

function arrow(cur: number | null, prev: number | null, tolerance: number): string {
    if (cur === null || prev === null || Number.isNaN(cur) || Number.isNaN(prev)) {
        return '';
    }
    if (Math.abs(cur - prev) <= tolerance) {
        return 'Stable';
    }
    return cur > prev ? '↑' : '↓';
}

function previousQuarter(q: Quarter): Quarter {
    return q.q === 1 ? quarter(`${q.fy - 1}-Q4`) : quarter(`${q.fy}-Q${q.q - 1}`);
}

/** How a quarter reads in prose: "Q2 FY27 (Oct–Dec), quarter to date". */
function scopeLabel(q: Quarter | null): string {
    if (!q) {
        return 'all time (since December 2025)';
    }
    return q.key === currentQuarter().key ? `${q.label} to date` : q.label;
}

// Shapes of the wave-1 stats bodies (they are typed Record<string, unknown> there).
interface IncidentStats {
    total: number;
    open: number;
    closed: number;
    closure_rate: number;
    overdue_14: number;
    overdue_21: number;
    overdue_28: number;
    choking: number;
    by_level: { level: string; count: number }[];
    by_location: { location: string; count: number }[];
}
interface MedicationStats {
    total: number;
    given: number;
    refused: number;
    missed: number;
    needs_review: number;
    excluded: number;
    compliance_rate: number;
    refused_by_medicine: { medicine_name: string; count: number }[];
    missed_by_medicine: { medicine_name: string; count: number }[];
}
interface GoalStats {
    total: number;
    active: number;
    overdue_reviews: number;
    reviewed: number;
    no_review_date: number;
    review_compliance_rate: number;
    by_service_area: { serviceArea: string; count: number }[];
}
interface TendableStats {
    total: number;
    average_score: number | null;
    by_type: { type: string; count: number; averageScore: number | null }[];
    by_site: { site: string; count: number; averageScore: number | null }[];
    category_averages: { category: string; averageScore: number | null; inspections: number }[];
    actions: { open: number; overdue: number; completed: number };
}

const incStats = (q: Quarter | null) => incidentStats(q) as unknown as IncidentStats;
const medStats = (q: Quarter | null) => medicationStats(q) as unknown as MedicationStats;
const golStats = (q: Quarter | null) => goalStats(q) as unknown as GoalStats;

/**
 * Budgeted income / expenses, current month pro rata, over some months of the
 * financial year ending `fy` (all of its started months when `months` is
 * omitted). Null when that FY has no budget loaded.
 */
function budgetOver(fy: number, months: string[] | null = null): { revenue: number; expenses: number; margin: number } | null {
    const by = budgetYear(fyLabel(fy)) as { lines: { kind: string; section: string | null; months: Record<string, number> }[] } | null;
    if (!by) {
        return null;
    }
    let revenue = 0;
    let expenses = 0;
    for (const l of by.lines) {
        if (l.kind !== 'line') {
            continue;
        }
        for (const [m, v] of Object.entries(l.months)) {
            const mm = Number(m);
            const key = `${mm >= 7 ? fy - 1 : fy}-${String(mm).padStart(2, '0')}`;
            if (months && !months.includes(key)) {
                continue;
            }
            const frac = monthFraction(key);
            if (!frac) {
                continue;
            }
            if (l.section === 'Income') {
                revenue += (v || 0) * frac;
            } else if (l.section === 'Cost of Sales' || l.section === 'Operating Expenses') {
                expenses += (v || 0) * frac;
            }
        }
    }
    return revenue > 0 ? { revenue, expenses, margin: round1(((revenue - expenses) / revenue) * 100) } : null;
}
const budgetYtd = () => budgetOver(currentFy());

/** "30 Sep 2026". */
const dayLabel = (d: Date): string => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Operating margin for the financial year to date as at the end of a quarter
 * (1 July of the quarter's FY through its last day, or today for the current
 * quarter), with the budget over exactly the same months. The current quarter
 * gives the same figures as the finance page's FY-to-date margin.
 */
function marginFyToDate(q: Quarter) {
    const months = fyToDateMonths(q);
    const rows = months.map(monthTotals).filter((m): m is MonthTotals => !!m);
    const revenue = rows.reduce((s, m) => s + m.revenue, 0);
    const expenses = rows.reduce((s, m) => s + m.expenditure, 0);
    const isCurrent = q.key === currentQuarter().key;
    return {
        revenue,
        expenses,
        margin: revenue > 0 ? Math.round(((revenue - expenses) / revenue) * 1000) / 10 : 0,
        budget: budgetOver(q.fy, months),
        /** "FY 2026-27 to date" / "FY 2026-27 to 30 Sep 2026". */
        scope: `FY ${fyLabel(q.fy)} ${isCurrent ? 'to date' : `to ${dayLabel(q.end)}`}`,
    };
}

/** Residential houses, lowest occupancy first. */
function houses() {
    const occ = occupancyStats();
    const rows = occ.facilities.filter(f => f.capacity).sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0) || a.name.localeCompare(b.name));
    return { totals: occ.totals, rows, vacant: occ.totals.capacity - occ.totals.occupied };
}

/** Distinct staff with at least one expired / missing compliance item. */
function staffWithGaps() {
    const cats = complianceByCategory(false).categories as {
        key: string;
        name: string;
        rate: number;
        items: { name: string; expired: number; missing: number; total: number; rate: number; expired_employees: { employee_id: string }[]; missing_employees: { employee_id: string }[] }[];
    }[];
    const any = new Set<string>();
    const expired = new Set<string>();
    const missing = new Set<string>();
    const byCat = new Map<string, Set<string>>();
    for (const c of cats) {
        const set = new Set<string>();
        byCat.set(c.name, set);
        for (const i of c.items) {
            i.expired_employees.forEach(e => (expired.add(e.employee_id), any.add(e.employee_id), set.add(e.employee_id)));
            i.missing_employees.forEach(e => (missing.add(e.employee_id), any.add(e.employee_id), set.add(e.employee_id)));
        }
    }
    return { cats, byCat, staff: peopleSummary(null).active_staff, any: any.size, expired: expired.size, missing: missing.size };
}

// ============================================================ board dashboard

/** GET /api/board/dashboard — the twelve figures, identical to the staff dashboard tiles for the same quarter. */
export function boardDashboard(q: Quarter | null): Record<string, unknown> {
    const inc = incStats(q);
    const people = peopleSummary(q);
    return {
        quarter: q ? q.key : null,
        quality: {
            incidents: inc.total,
            open_incidents: inc.open,
            medication_compliance_rate: medStats(q).compliance_rate,
            care_plan_review_rate: golStats(q).review_compliance_rate,
        },
        people: {
            active_staff: people.active_staff,
            training_completion_rate: people.training_completion_rate,
            training_in_progress: people.training_in_progress,
            turnover_rate_12m: people.turnover_rate_12m,
        },
        finance: financeBoardTiles(q),
    };
}

// =========================================================== quarterly report

type Rag = '' | 'green' | 'amber' | 'red' | 'blue';
const RAGS: Rag[] = ['', 'green', 'amber', 'red', 'blue'];

interface KpiEntry {
    target?: string;
    rag?: string;
    notes?: string;
    trend?: string;
    result_override?: string;
}
interface RiskRow {
    risk: string;
    cause: string;
    impact: string;
    controls: string;
    likelihood: string;
    consequence: string;
    action: string;
}
interface DecisionRow {
    decision: string;
    rationale: string;
    impact_if_deferred: string;
    recommendation: string;
}
interface ProjectRow {
    initiative: string;
    pillar: string;
    milestone_due: string;
    status: string;
    benefits: string;
    owner: string;
}
interface SavedReport {
    voice_of_client: string;
    executive_summary: string;
    kpi_entries: Record<string, KpiEntry>;
    pillar_summaries: Record<string, string>;
    pillar1_risks: RiskRow[];
    pillar3_risks: RiskRow[];
    decisions: DecisionRow[];
    decisions_summary: string;
    /** null = never touched, so the previous quarter's portfolio carries forward. */
    project_portfolio: ProjectRow[] | null;
    updated_at: string | null;
}
interface AutoKpi {
    result: string;
    source_note?: string | null;
}

const P1 = 'Pillar 1 — Clients & Quality';
const P2 = 'Pillar 2 — People';
const P3 = 'Pillar 3 — Financial Sustainability';
const P4 = 'Pillar 4 — Partnerships & Collaboration';
const P5 = 'Pillar 5 — Governance';

/** QuarterlyReportService::buildPillars schema — keys and labels verbatim, order fixed (the template uses the index). */
const PILLARS: { name: string; kpis: [key: string, label: string][] }[] = [
    {
        name: P1,
        kpis: [
            ['clients_count', 'Clients (total)'],
            ['goals_progression', 'Client goals progression'],
            ['cultural_support_plans', 'Aboriginal clients with Cultural Support Plans %'],
            ['incidents_total', 'Number of incidents'],
            ['incidents_reportable', 'Reportable incidents'],
            ['incidents_closure_rate', 'Incident closure rate'],
            ['clinical_governance_audits', 'Clinical governance audits completed on schedule %'],
            ['whs_audits', 'WHS Audits completed on schedule %'],
        ],
    },
    {
        name: P2,
        kpis: [
            ['vacancy_rate', 'Vacancy rate % and time-to-fill'],
            ['turnover_rate', '12-month turnover %'],
            ['headcount', 'Head count (FT / PPT / Casual)'],
            ['training_completion', 'Mandatory & Essential training completion %'],
            ['performance_reviews', 'Performance Review Completion'],
            ['atsi_percent', 'ATSI workforce %'],
            ['worker_comp_claims', 'Worker compensation claims new/open'],
        ],
    },
    {
        name: P3,
        kpis: [
            ['operating_margin', 'Operating margin YTD % vs budget'],
            ['sil_occupancy', 'Supported accommodation & respite occupancy %'],
            ['days_ar', 'Days Accounts receivable outstanding'],
            ['expenditure_vs_sa', 'Expenditure vs funding agreements'],
            ['unbillable_time', '% unbillable time'],
            ['new_referrals', 'New referrals, to new clients'],
        ],
    },
    {
        name: P4,
        kpis: [
            ['advocacy_forums', 'Advocacy/reform forums attended & outcomes'],
            ['brand_reach', 'Brand reach — website visits / social engagement'],
            ['sector_meetings', 'Sector Meetings attended'],
        ],
    },
    {
        name: P5,
        kpis: [
            ['compliance_on_time', 'Compliance obligations met on time %'],
            ['strategic_risk_mgmt', 'Strategic Risk Management'],
            ['clinical_governance_actions', 'Clinical governance committee actions closed %'],
            ['finance_audit_actions', 'Finance and Audit committee actions closed %'],
            ['people_governance_actions', 'People, Governance Culture and Risk actions closed %'],
        ],
    },
];

/** Every number the report uses for one quarter, straight from the generators. */
function reportFacts(q: Quarter) {
    const s = scopeOf(q);
    const inc = incStats(q);
    const people = peopleSummary(q);
    const eh = ehSummary();
    const days = Math.max(1, (s.to.getTime() - s.from.getTime()) / 86400000);
    const isCurrent = q.key === currentQuarter().key;
    return {
        q,
        isCurrent,
        days,
        clients: activeClients().length,
        inc,
        incPerDay: inc.total / days,
        reportable: incidentReportable(q),
        med: medStats(q),
        goals: golStats(q),
        progression: goalProgression(q),
        whs: whsAuditSummary(q),
        people,
        // Roster figures as at the quarter's last day (today, for the current
        // quarter — the same as the live tiles): head count, ATSI share and
        // 12-month rolling turnover are all reconstructed from the roster, so
        // a closed quarter quotes its own position, not today's.
        asAt: isCurrent ? 'today' : dayLabel(q.end),
        headcount: isCurrent ? people.active_staff : people.headcount_at_period_end,
        byType: isCurrent ? eh.employees.by_type : people.headcount_at_period_end_by_type,
        atsi: people.atsi_at_period_end,
        turnover: people.turnover_at_period_end,
        // Current-only: recruitment has no history, so the vacancy rate is today's.
        vacancyPct: round1((people.recruitment.open / Math.max(1, people.active_staff + people.recruitment.open)) * 100),
        compliance: complianceSummary(),
        // Every quarter reports the FY to date as at its own end (the current
        // one: to today), against the budget over the same months, so a Q4
        // report never quotes the next financial year's numbers. `quarter` is
        // the quarter on its own, quoted alongside for context.
        fytd: marginFyToDate(q),
        quarterMargin: profitability(q),
        ar: accountsReceivable(),
        occupancy: houses(),
        referralsIn: referrals().filter(r => !r.archived && new Date(r.dateReceived) >= s.from && new Date(r.dateReceived) <= s.to),
    };
}
type Facts = ReturnType<typeof reportFacts>;

/** QuarterlyReportService::computeAutoKpis, from the same generators as the pages. */
function autoKpis(f: Facts): Record<string, AutoKpi> {
    const out: Record<string, AutoKpi> = {};
    const { inc } = f;
    out['incidents_total'] = { result: String(inc.total) };
    out['incidents_closure_rate'] = { result: `${inc.closure_rate}%` };
    if (inc.by_level.length) {
        out['incidents_reportable'] = { result: String(f.reportable.count), source_note: 'Levels ending in "R" (reportable)' };
        out['incidents_total'].source_note = 'By level — ' + inc.by_level.map(l => `${l.level}: ${l.count}`).join(', ');
    }
    out['clients_count'] = { result: String(f.clients), source_note: f.isCurrent ? 'Current (as of last sync)' : 'Current (as at today — client history not held)' };
    if (f.progression.reviewed > 0) {
        out['goals_progression'] = {
            result: `${f.progression.achievedPct}% achieved, ${f.progression.partialPct}% partial`,
            source_note: `Based on ${f.progression.reviewed} goals reviewed in quarter`,
        };
    }
    if (f.whs.audits > 0) {
        out['whs_audits'] = {
            result: f.whs.averageScore !== null ? `${f.whs.audits} audits, avg ${f.whs.averageScore}%` : `${f.whs.audits} audits`,
            source_note: 'Completed WHS audits in quarter (Tendable)',
        };
    }
    out['turnover_rate'] = {
        result: `${f.turnover.rate}%`,
        source_note: `${f.turnover.leavers} leavers ÷ ${f.turnover.avg_headcount} average headcount, 12 months to ${f.asAt} (Employment Hero)`,
    };
    out['atsi_percent'] = { result: `${f.atsi.atsi_percent}%`, source_note: `${f.atsi.atsi_count} of ${f.atsi.total_active} staff as at ${f.asAt}` };
    out['headcount'] = {
        result: String(f.headcount),
        source_note: `FT ${f.byType.fulltime} · PPT ${f.byType.parttime} · Casual ${f.byType.casual} as at ${f.asAt}`,
    };
    const rec = f.people.recruitment;
    out['vacancy_rate'] = {
        result: `${f.vacancyPct}%` + (rec.avg_time_to_fill_days !== null ? ` / ${rec.avg_time_to_fill_days} days to fill` : ''),
        source_note: `${plural(rec.open, 'open role')} ÷ (${f.people.active_staff} staff + ${rec.open} open); fills in the last 90 days (current)`,
    };
    out['training_completion'] = {
        result: `Mandatory ${f.compliance.mandatory_rate}%, Essential ${f.compliance.essential_rate}%`,
        source_note: 'Current (point-in-time); Essential scoped to frontline program staff',
    };
    out['operating_margin'] = {
        result: `${f.fytd.margin}%`,
        source_note:
            `${f.fytd.scope}: rev ${money(f.fytd.revenue)} / exp ${money(f.fytd.expenses)}; ` +
            (f.fytd.budget ? `budget ${f.fytd.budget.margin}% on rev ${money(f.fytd.budget.revenue)}` : 'no budget loaded') +
            (f.q.q === 1 ? '.' : `. ${f.isCurrent ? 'Quarter to date' : 'Quarter alone'} ${f.quarterMargin.operating_margin_percent}%.`),
    };
    out['days_ar'] = { result: `${f.ar.avg_days_outstanding} days`, source_note: `${money(f.ar.total_outstanding)} outstanding across ${f.ar.open_invoices} invoices (current)` };
    if (f.occupancy.totals.capacity > 0) {
        out['sil_occupancy'] = {
            result: `${f.occupancy.totals.rate}%`,
            source_note: `${f.occupancy.totals.occupied} of ${f.occupancy.totals.capacity} beds occupied (current)`,
        };
    }
    return out;
}

const ragAtLeast = (v: number, green: number, amber: number): Rag => (v >= green ? 'green' : v >= amber ? 'amber' : 'red');
const ragAtMost = (v: number, green: number, amber: number): Rag => (v <= green ? 'green' : v <= amber ? 'amber' : 'red');

/** "Three weeks into" / "In the first week of" — how far into the current quarter we are. */
function weeksInto(q: Quarter): string {
    const days = Math.max(0, (now().getTime() - q.start.getTime()) / 86400000);
    if (days < 7) {
        return 'In the first week of';
    }
    const words = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen'];
    const w = Math.min(13, Math.round(days / 7));
    const phrase = `${days / 7 < w - 0.15 ? 'nearly ' : ''}${words[w]} week${w === 1 ? '' : 's'} into`;
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

const monthName = (offset: number): string => new Date(now().getFullYear(), now().getMonth() + offset, 1).toLocaleString('en-AU', { month: 'long' });

/** Narrative that differs from quarter to quarter: index 0 is the current quarter, 1 the one before, and so on. */
const VOICES = [
    'An elderly woman supported by our Aged Care team on the Lands told her support worker, through an interpreter, that the Ngangkari visits and the new respite arrangement meant she could stay on country rather than travel to town for care. "I can stay here with my family and my grandchildren, and the Ngangkari look after my spirit," she said. Her family has asked for the arrangement to continue over summer.',
    'A young mother supported by the Domestic & Family Violence team said the safe-house transport and the Child Nutrition workers\' visits helped her keep her children well while she settled back in community. "They didn\'t rush me. They listened, and they came back," she said. She now attends the women\'s group at the community centre each week.',
    'A senior weaver from Irrunytju said the Tjanpi field trip to collect grass with her granddaughters was the first time in years three generations had worked together on country. "Our baskets went to the gallery in the city and the money came straight back to our families," she said. Two of the girls have since joined the weaving workshops.',
    'A teenage girl in the Youth Program at Pukatja said the school-holiday bush trips with senior women helped her learn the stories for her grandmother\'s country. "I know my place now, and I teach the little ones," she said. Her school has reported better attendance this term.',
    'A young man supported by the Disability team in Amata used his NDIS plan to fund a ramp and a supported trip to see family in Kaltjiti. His mother said, "Before, he was stuck inside. Now he sits with everyone at the fire." His support worker is helping him plan a second visit.',
];

const CONTEXT = [
    '',
    'with the dry-season program of community visits and the Tjanpi field trips completed on schedule',
    'and the financial year closed on time with the FY27 budget adopted by the Board',
    'despite summer road closures that cut access to the WA communities for several weeks',
    'in the first quarter reported through the executive dashboard (client figures cover December only)',
];

const P4_SUMMARIES = [
    'Active in tri-state DFV reform and remote aged care workforce consultations.',
    'Hosted the annual Tjanpi field program review with partner art centres.',
    'Presented to the remote aged care reform consultation; renewed the regional health service partnership for Ngangkari referrals.',
    'Joined the tri-state DFV reform roundtable; Tjanpi featured in a national gallery exhibition.',
    'Signed a data-sharing agreement with the regional health service; briefed new state ministers on the NPY Lands.',
];

const P5_SUMMARIES = [
    '', // current quarter: built from the figures below
    'Annual compliance calendar on track; external audit management letter actions closed.',
    'Year-end audit fieldwork completed with no significant findings; Board skills matrix refreshed.',
    'Risk register refreshed and presented to the Finance, Audit & Risk Committee; constitution review under way.',
    'Governance calendar adopted for 2026; Clinical Governance Committee terms of reference updated.',
];

const PAST_DECISIONS: DecisionRow[][] = [
    [],
    [
        {
            decision: 'Adopt the revised Client Incident Management Policy',
            rationale: 'Aligns review timeframes with NDIS Practice Standards and aged care requirements',
            impact_if_deferred: 'Inconsistent incident handling across programs',
            recommendation: 'Adopt',
        },
    ],
    [
        {
            decision: 'Approve the FY27 operating budget',
            rationale: 'Balances growth in aged care and NDIS services against the end of two time-limited youth grants',
            impact_if_deferred: 'Programs start the year without approved spending limits',
            recommendation: 'Approve, with a mid-year review in February',
        },
        {
            decision: 'Appoint the external auditor for FY27',
            rationale: 'Tender run by the Finance, Audit & Risk Committee',
            impact_if_deferred: 'Audit fieldwork could not be scheduled before the remote travel season',
            recommendation: 'Appoint the recommended firm for three years',
        },
    ],
    [
        {
            decision: 'Endorse the business case for remote staff housing upgrades in Amata and Kaltjiti (Fregon)',
            rationale: 'Housing is the main reason remote roles stay vacant',
            impact_if_deferred: 'Continued reliance on fly-in staff at higher cost',
            recommendation: 'Endorse and seek co-funding from the housing program',
        },
    ],
    [
        {
            decision: 'Approve the executive dashboard as the source for quarterly Board reporting',
            rationale: 'One set of figures for management and the Board, refreshed nightly',
            impact_if_deferred: 'Board papers keep being assembled by hand from five systems',
            recommendation: 'Approve from the Q3 FY26 Board meeting',
        },
    ],
];

/**
 * The saved draft a CEO would have for each quarter since the dashboard went
 * live: targets, RAG, trends, narrative, risks, decisions and projects. RAG
 * and narrative numbers are computed from the same facts as the auto KPIs, so
 * they always agree.
 */
function seededReport(q: Quarter): SavedReport | null {
    const quarters = allQuarters();
    const idx = quarters.findIndex(x => x.key === q.key);
    if (idx < 0) {
        return null;
    }
    const isCurrent = idx === 0;
    const v = Math.min(idx, VOICES.length - 1);
    const f = reportFacts(q);
    const prev = reportFacts(previousQuarter(q));
    const r = rng(`ceo-report:${q.key}`);
    const { inc, people, compliance, fytd, ar, occupancy } = f;

    // Trend: the same 12-month rolling rate 91 days earlier. Leaver history
    // runs well before the data window, so both ends are full 12-month rates.
    const day90 = ymd(new Date(new Date(`${f.turnover.to}T12:00:00`).getTime() - 91 * 86400000));
    const turnover90 = turnoverRate12m(day90);
    const turnover = f.turnover.rate;
    const recruitment = people.recruitment;
    const vacancyPct = f.vacancyPct;
    const fill = recruitment.avg_time_to_fill_days;
    const lowHouse = occupancy.rows[0];
    const topOpenSite = tally(
        incidentsIn(scopeOf(q)).filter(i => i.open),
        i => i.location,
    )[0];
    const ndis = compliance.ndis_worker_screening;
    const ndisGap = ndis.expired + ndis.missing;
    const agedOpen = inc.overdue_14 + inc.overdue_21 + inc.overdue_28;

    // Cultural Support Plans have been rolling out since the dashboard went live.
    const cultural = r.int(68 - idx * 5, 76 - idx * 5);
    const cgAudits = r.int(80, 92);
    // The performance review cycle runs April to March.
    const perfMilestone = [0, 30, 50, 90, 90][q.q];
    const perfReviews = q.q === 1 ? r.int(31, 40) : q.q === 2 ? r.int(52, 62) : q.q === 3 ? r.int(86, 93) : r.int(91, 97);
    const newClaims = r.int(0, 2);
    const openClaims = newClaims + r.int(1, 2);
    const forums = r.int(3, 5);
    const meetings = r.int(9, 15);
    const visits = r.float(14, 22, 1);
    const reach = r.int(38, 61);
    const cgActions = r.int(70, 84);
    const faActions = r.int(92, 100);
    const pgActions = r.int(84, 95);
    const unbillable = r.float(6.4, 9.2, 1);
    const onTime = r.int(95, 99);
    const referralGreen = isCurrent ? Math.round(10 * elapsed(q)) : 10;

    const kpi: Record<string, KpiEntry> = {
        clients_count: { target: '150', rag: f.clients >= 150 ? 'green' : 'amber', trend: 'Stable', notes: 'Across disability, aged care, Ngangkari and youth programs.' },
        goals_progression: {
            target: '≥ 40% achieved',
            rag: f.progression.reviewed ? ragAtLeast(f.progression.achievedPct, 40, 30) : '',
            trend: arrow(f.progression.achievedPct, prev.progression.reviewed ? prev.progression.achievedPct : null, 2),
            notes: '',
        },
        cultural_support_plans: {
            target: '100%',
            rag: 'amber',
            trend: '↑',
            result_override: `${cultural}%`,
            notes: 'Plans developed with family and Ngangkari input; remaining plans scheduled with community visits.',
        },
        incidents_total: {
            target: 'Trend ↓',
            rag: 'blue',
            trend: arrow(f.incPerDay, prev.incPerDay, 0.05),
            notes: topOpenSite ? `Highest open count at ${topOpenSite[0]}.` : '',
        },
        incidents_reportable: {
            target: '0',
            rag: f.reportable.count === 0 ? 'green' : f.reportable.count <= 2 ? 'amber' : 'red',
            trend: arrow(f.reportable.count, prev.reportable.count, 0),
            notes: f.reportable.count ? 'All notified to the regulator within timeframes.' : '',
        },
        incidents_closure_rate: {
            target: '≥ 85%',
            // Early in a quarter the open incidents are simply recent, not late.
            rag: isCurrent && agedOpen === 0 && inc.closure_rate < 85 ? 'amber' : ragAtLeast(inc.closure_rate, 85, 70),
            trend: arrow(inc.closure_rate, prev.inc.closure_rate, 2),
            notes: inc.overdue_28
                ? `${plural(inc.overdue_28, 'incident')} open beyond 28 days.`
                : isCurrent && inc.open && agedOpen === 0
                  ? 'Every open incident is under 14 days old; reviews are on schedule.'
                  : '',
        },
        clinical_governance_audits: { target: '100%', rag: ragAtLeast(cgAudits, 95, 80), trend: 'Stable', result_override: `${cgAudits}%`, notes: cgAudits < 95 ? 'Deferred audits rescheduled with the next community visit.' : '' },
        whs_audits: {
            target: '≥ 90% avg score',
            rag: f.whs.averageScore === null ? '' : ragAtLeast(f.whs.averageScore, 90, 80),
            trend: arrow(f.whs.averageScore, prev.whs.averageScore, 1),
            notes: '',
        },
        vacancy_rate: {
            target: '< 8% / ≤ 35 days',
            rag: fill === null ? ragAtMost(vacancyPct, 8, 12) : fill <= 35 && vacancyPct < 8 ? 'green' : fill <= 45 ? 'amber' : 'red',
            trend: '',
            notes: `${plural(recruitment.open, 'open role')}, ${recruitment.overdue} past the ${recruitment.target_days}-day target${isCurrent ? '' : ' (as at today)'}.`,
        },
        turnover_rate: {
            target: '< 25%',
            rag: ragAtMost(turnover, 25, 32),
            trend: arrow(turnover, turnover90, 0.5),
            notes: 'Casual remote roles account for most exits.',
        },
        headcount: { target: '210', rag: 'blue', trend: 'Stable', notes: '' },
        training_completion: {
            target: '≥ 90% / ≥ 80%',
            rag: compliance.mandatory_rate >= 90 && compliance.essential_rate >= 80 ? 'green' : compliance.mandatory_rate < 75 ? 'red' : 'amber',
            trend: '',
            notes: ndisGap
                ? isCurrent
                    ? `${plural(ndisGap, 'staff member')} missing or expired NDIS Worker Screening Clearance — escalated to managers.`
                    : `As at today, ${plural(ndisGap, 'staff member')} missing or expired NDIS Worker Screening Clearance (current).`
                : '',
        },
        performance_reviews: {
            target: '90% by 31 March',
            rag: q.q <= 2 ? (perfReviews >= perfMilestone ? 'green' : 'amber') : ragAtLeast(perfReviews, 90, 80),
            trend: '↑',
            result_override: `${perfReviews}%`,
            notes: q.q <= 2 ? `Ahead of the ${perfMilestone}% mid-cycle milestone.` : q.q === 3 ? 'Cycle closed 31 March; remaining reviews booked.' : 'Final result for the cycle.',
        },
        atsi_percent: { target: '≥ 40%', rag: ragAtLeast(f.atsi.atsi_percent, 40, 30), trend: 'Stable', notes: 'Anangu employment pathway roles counted on commencement.' },
        worker_comp_claims: {
            target: '0 new',
            rag: newClaims === 0 ? 'green' : 'amber',
            trend: '',
            result_override: `${newClaims} new / ${openClaims} open`,
            notes: newClaims ? 'Manual handling claim; return-to-work plan in place.' : '',
        },
        operating_margin: {
            target: fytd.budget ? `≥ ${fytd.budget.margin}% (budget YTD)` : '≥ 2%',
            rag: ragAtLeast(fytd.margin, fytd.budget ? fytd.budget.margin : 2, 0),
            trend: '',
            notes: '',
        },
        sil_occupancy: {
            target: '≥ 90%',
            rag: ragAtLeast(occupancy.totals.rate, 90, 80),
            trend: 'Stable',
            notes: lowHouse && lowHouse.rate !== null && lowHouse.rate < 90 ? `${lowHouse.name} at ${lowHouse.rate}%; referrals prioritised.` : '',
        },
        days_ar: { target: '≤ 30 days', rag: ragAtMost(ar.avg_days_outstanding, 30, 45), trend: '', notes: '' },
        expenditure_vs_sa: { target: 'Within 5%', rag: 'green', trend: 'Stable', result_override: 'Within 3% across all agreements', notes: '' },
        unbillable_time: {
            target: '< 10%',
            rag: ragAtMost(unbillable, 10, 12),
            trend: 'Stable',
            result_override: `${unbillable}%`,
            notes: 'Mostly travel time between communities.',
        },
        new_referrals: {
            target: '10 per quarter',
            rag: ragAtLeast(f.referralsIn.length, referralGreen, isCurrent ? Math.round(6 * elapsed(q)) : 6),
            trend: '',
            result_override: `${f.referralsIn.length} received, ${f.referralsIn.filter(x => x.companyDecision === 'Accept').length} accepted`,
            notes: isCurrent ? `On pace for the quarter (${referralGreen} expected by now); decisions due within 14 days of receipt.` : '',
        },
        advocacy_forums: {
            target: '3 per quarter',
            rag: 'green',
            trend: '',
            result_override: `${forums} forums attended`,
            notes: 'Tri-state DFV reform roundtable; remote aged care workforce consultation; NDIS remote pricing review.',
        },
        brand_reach: { target: '', rag: 'blue', trend: '↑', result_override: `${visits}k website visits / ${reach}k social reach`, notes: 'Tjanpi exhibition coverage drove social reach.' },
        sector_meetings: { target: '10', rag: meetings >= 10 ? 'green' : 'amber', trend: '', result_override: String(meetings), notes: '' },
        compliance_on_time: { target: '100%', rag: 'green', trend: 'Stable', result_override: `${onTime}%`, notes: '' },
        strategic_risk_mgmt: { target: 'Quarterly review', rag: 'green', trend: '', result_override: 'Risk register reviewed by Finance, Audit & Risk Committee', notes: '' },
        clinical_governance_actions: {
            target: '≥ 90%',
            rag: ragAtLeast(cgActions, 90, 75),
            trend: '',
            result_override: `${cgActions}%`,
            notes: cgActions < 90 ? 'Medication audit actions outstanding at two respite sites.' : '',
        },
        finance_audit_actions: { target: '≥ 90%', rag: 'green', trend: '', result_override: `${faActions}%`, notes: '' },
        people_governance_actions: { target: '≥ 90%', rag: ragAtLeast(pgActions, 90, 75), trend: '', result_override: `${pgActions}%`, notes: pgActions < 90 ? 'Remote housing policy review carried to next meeting.' : '' },
    };

    const margin = `${fytd.margin}%`;
    const budgetPhrase = fytd.budget ? ` against a budgeted ${fytd.budget.margin}%` : '';
    const lowHousePhrase = lowHouse && lowHouse.rate !== null && lowHouse.rate < 90 ? `, with ${lowHouse.name} the main gap at ${lowHouse.rate}%` : '';
    const ndisPhrase = ndisGap
        ? `${plural(ndisGap, 'staff member')} ${ndisGap === 1 ? 'is' : 'are'} missing or expired NDIS Worker Screening Clearance and ${ndisGap === 1 ? 'has' : 'have'} been stood down from NDIS shifts until cleared.`
        : 'All NDIS-facing staff hold a current Worker Screening Clearance.';

    const executive = isCurrent
        ? [
              `${weeksInto(q)} ${q.label}, services continue across ${ORG.shortName}'s communities in the NT, SA and WA, with ${f.clients} active clients supported by ${people.active_staff} staff.`,
              `Quality: ${inc.total} incidents have been recorded quarter to date with a ${inc.closure_rate}% closure rate; ${inc.open} remain open${topOpenSite ? ` (most at ${topOpenSite[0]})` : ''}${inc.overdue_28 ? `, ${inc.overdue_28} of them beyond 28 days` : ', none yet beyond 28 days'}. Medication compliance is ${f.med.compliance_rate}%.`,
              `People: 12-month turnover is ${people.turnover_rate_12m}% and Aboriginal employment ${people.atsi_percent}%. Mandatory training sits at ${compliance.mandatory_rate}%. ${ndisPhrase}`,
              `Finance: the operating margin is ${margin} FY to date${budgetPhrase}, on revenue of ${money(fytd.revenue)}. Debtor days are ${ar.avg_days_outstanding}. Supported accommodation and respite occupancy is ${occupancy.totals.rate}%${lowHousePhrase}.`,
          ].join('\n\n')
        : [
              `${q.label} closed with steady service delivery across the NPY Lands, ${CONTEXT[Math.min(idx, CONTEXT.length - 1)]}; ${f.clients} clients are active today.`,
              `Quality: ${inc.total} incidents were recorded (${f.reportable.count} reportable) and ${inc.closure_rate}% are closed. Medication compliance was ${f.med.compliance_rate}% and care plan review compliance ${f.goals.review_compliance_rate}%.`,
              `People: 12-month turnover was ${turnover}% at quarter end (${f.turnover.leavers} leavers over an average ${f.turnover.avg_headcount} staff), and ${people.hires_in_period} staff started in the quarter against ${people.terminations_in_period} exits, leaving ${f.headcount} staff at ${f.asAt}.`,
              `Finance: the operating margin for ${fytd.scope} was ${margin}${budgetPhrase}, on revenue of ${money(fytd.revenue)}${q.q === 1 ? '' : `; the quarter alone ran at ${f.quarterMargin.operating_margin_percent}% on ${money(f.quarterMargin.revenue_ytd)}`}.`,
          ].join('\n\n');

    const pillarSummaries: Record<string, string> = {
        [P1]: `${inc.total} incidents (${inc.closure_rate}% closed); medication compliance ${f.med.compliance_rate}%; care plan review compliance ${f.goals.review_compliance_rate}%. Cultural Support Plans continue to roll out with family and Ngangkari input.`,
        [P2]: isCurrent
            ? `Headcount ${f.headcount} (FT ${f.byType.fulltime} · PPT ${f.byType.parttime} · Casual ${f.byType.casual}); 12-month turnover ${turnover}%. Remote recruitment remains the key pressure, with ${plural(recruitment.open, 'open role')}.`
            : `As at ${f.asAt}: headcount ${f.headcount} (FT ${f.byType.fulltime} · PPT ${f.byType.parttime} · Casual ${f.byType.casual}), 12-month turnover ${turnover}% and Aboriginal employment ${f.atsi.atsi_percent}%. Remote recruitment remained the key pressure.`,
        [P3]: isCurrent
            ? `Operating margin ${margin} FY to date${budgetPhrase}. Accounts receivable ${money(ar.total_outstanding)} (${ar.avg_days_outstanding} days).`
            : `Operating margin ${margin} for ${fytd.scope}${budgetPhrase}, on revenue of ${money(fytd.revenue)}${q.q === 1 ? '' : ` (quarter alone ${f.quarterMargin.operating_margin_percent}%)`}.`,
        [P4]: P4_SUMMARIES[Math.min(idx, P4_SUMMARIES.length - 1)],
        [P5]: isCurrent
            ? `Compliance calendar on track (${onTime}% of obligations met on time). Clinical Governance Committee action closure (${cgActions}%) is below target and is the focus of the ${monthName(1)} meeting.`
            : P5_SUMMARIES[Math.min(idx, P5_SUMMARIES.length - 1)],
    };

    const pillar1Risks: RiskRow[] = [
        {
            risk: 'Delayed close-out of incident reviews in remote communities',
            cause: 'Team leader vacancies in the WA communities and long travel times between sites',
            impact: 'Reportable incident timeframes at risk; regulator scrutiny',
            controls: 'Weekly incident huddle; Quality & Risk Advisor reviews every incident open more than 14 days',
            likelihood: 'Likely',
            consequence: 'Moderate',
            action: 'Backfill WA team leader role; fly-in review support until the backlog clears — COO',
        },
        {
            risk: 'Medication administration errors in respite settings',
            cause: 'Casual staff new to dose administration aids; limited pharmacy access on the Lands',
            impact: 'Client harm; regulatory breach',
            controls: 'Medication competency on induction; monthly medication audit',
            likelihood: 'Possible',
            consequence: 'Major',
            action: 'Refresher training for all respite staff; pharmacist visit schedule with the regional health service — Client Services Manager',
        },
    ];
    const pillar3Risks: RiskRow[] = [
        {
            risk: 'Delayed renewal of Commonwealth program funding beyond 30 June',
            cause: 'Federal grant round timing',
            impact: 'Program revenue uncertain for FY28 planning; staff retention risk',
            controls: 'Early engagement with funders; reserves policy of three months operating costs',
            likelihood: 'Possible',
            consequence: 'Major',
            action: 'Brief funders this quarter; scenario budget for FY28 — CEO',
        },
        {
            risk: 'Rising debtor days on state government invoices',
            cause: 'Invoice approval delays in agency portals',
            impact: 'Cash flow pressure over the summer quarter',
            controls: 'Fortnightly debtor review; escalation to agency contract managers',
            likelihood: 'Likely',
            consequence: 'Minor',
            action: 'Clear invoices older than 60 days by month end — CFO',
        },
    ];
    const decisions: DecisionRow[] = isCurrent
        ? [
              {
                  decision: 'Approve two additional remote-based DFV case manager positions (Warakurna and Pukatja)',
                  rationale: 'Demand for DFV support has grown and the current team covers about 350,000 square kilometres',
                  impact_if_deferred: 'Longer waits for women and children seeking safety over the summer peak',
                  recommendation: 'Approve, funded from the DFV program allocation with a 12-month review',
              },
              {
                  decision: 'Endorse the Tjanpi Desert Weavers online retail strategy',
                  rationale: 'Gallery sales are seasonal; an online channel smooths income for weavers',
                  impact_if_deferred: 'Miss the Christmas trading period',
                  recommendation: 'Endorse with a capped implementation budget of $45,000',
              },
          ]
        : PAST_DECISIONS[Math.min(idx, PAST_DECISIONS.length - 1)];
    const portfolio: ProjectRow[] = [
        {
            initiative: 'Ngangkari Program outcomes evaluation',
            pillar: P1,
            milestone_due: isCurrent ? 'Feb 2027' : 'Dec 2026',
            status: 'green',
            benefits: 'Evidence base for program funding renewal',
            owner: LEADERSHIP.care.name,
        },
        {
            initiative: 'Remote staff housing upgrade — Amata and Kaltjiti (Fregon)',
            pillar: P2,
            milestone_due: 'Mar 2027',
            status: isCurrent ? 'amber' : 'green',
            benefits: 'Retain remote staff; reduce fly-in costs',
            owner: LEADERSHIP.coo.name,
        },
        {
            initiative: 'Payroll and finance system integration',
            pillar: P3,
            milestone_due: 'Dec 2026',
            status: isCurrent ? 'red' : idx === 1 ? 'amber' : 'green',
            benefits: 'Single source for labour cost reporting; fewer manual journals',
            owner: LEADERSHIP.cfo.name,
        },
        ...(idx <= 2
            ? [
                  {
                      initiative: 'Client management system replacement',
                      pillar: P1,
                      milestone_due: 'Jun 2027',
                      status: 'blue',
                      benefits: 'Mobile case notes for remote workers; better incident data',
                      owner: LEADERSHIP.coo.name,
                  },
              ]
            : []),
    ];

    const updated = isCurrent
        ? new Date(Math.min(now().getTime() - 2 * 3600000, Math.max(q.start.getTime() + 2 * 86400000 + 16.7 * 3600000, now().getTime() - 3 * 86400000)))
        : new Date(Math.min(now().getTime() - 3600000, q.end.getTime() + 13 * 86400000 + 10.25 * 3600000));

    return {
        voice_of_client: VOICES[v],
        executive_summary: executive,
        kpi_entries: kpi,
        pillar_summaries: pillarSummaries,
        pillar1_risks: pillar1Risks,
        pillar3_risks: pillar3Risks,
        decisions,
        decisions_summary: isCurrent
            ? 'Two decisions are sought from the Board this quarter.'
            : decisions.length === 1
              ? 'One decision was put to the Board and adopted.'
              : `${decisions.length === 2 ? 'Two' : decisions.length} decisions were put to the Board and adopted.`,
        project_portfolio: portfolio,
        updated_at: iso(updated),
    };
}

const reportStore = (): Record<string, SavedReport> => getState('reports:ceo', () => ({}) as Record<string, SavedReport>);

/** The quarter's saved state: edits made in this tab, else the seeded draft, else null (never saved). */
function savedReport(q: Quarter): SavedReport | null {
    return reportStore()[q.key] ?? seededReport(q);
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const rag = (v: unknown): Rag => (RAGS.includes(v as Rag) ? (v as Rag) : '');

/** QuarterlyReportService::buildReport. */
export function ceoReport(q: Quarter): Record<string, unknown> {
    const saved = savedReport(q);
    const auto = autoKpis(reportFacts(q));
    const entries = saved?.kpi_entries ?? {};
    const summaries = saved?.pillar_summaries ?? {};

    const pillars = PILLARS.map(p => ({
        name: p.name,
        summary: summaries[p.name] ?? '',
        kpis: p.kpis.map(([key, label]) => {
            const a = auto[key];
            const m = entries[key] ?? {};
            return {
                key,
                label,
                target: str(m.target),
                result: m.result_override !== undefined && m.result_override !== null ? str(m.result_override) : (a?.result ?? ''),
                trend: str(m.trend),
                rag: rag(m.rag),
                notes: str(m.notes),
                auto: !!a,
                source_note: a?.source_note ?? null,
            };
        }),
    }));

    let portfolio = saved?.project_portfolio ?? null;
    if (portfolio === null) {
        portfolio = savedReport(previousQuarter(q))?.project_portfolio ?? [];
    }

    const n = now();
    const start = q.start.getTime() < scopeOf(null).from.getTime() ? scopeOf(null).from : q.start;
    const end = q.end.getTime() > n.getTime() && q.start.getTime() <= n.getTime() ? n : q.end;

    return {
        quarter: q.key,
        period: { start: ymd(start), end: ymd(end), label: q.label },
        voice_of_client: saved?.voice_of_client ?? '',
        executive_summary: saved?.executive_summary ?? '',
        pillars,
        pillar1_risks: saved?.pillar1_risks ?? [],
        pillar3_risks: saved?.pillar3_risks ?? [],
        decisions: saved?.decisions ?? [],
        decisions_summary: saved?.decisions_summary ?? '',
        project_portfolio: portfolio,
        updated_at: saved?.updated_at ?? null,
    };
}

/** QuarterlyReportService::saveFromPayload — top-level fields present in the payload replace the stored ones. */
function saveCeoReport(q: Quarter, body: any): Record<string, unknown> {
    const p = body && typeof body === 'object' ? body : {};
    const base: SavedReport = savedReport(q) ?? {
        voice_of_client: '',
        executive_summary: '',
        kpi_entries: {},
        pillar_summaries: {},
        pillar1_risks: [],
        pillar3_risks: [],
        decisions: [],
        decisions_summary: '',
        project_portfolio: null,
        updated_at: null,
    };
    const next: SavedReport = { ...base };
    const has = (k: string) => Object.prototype.hasOwnProperty.call(p, k);
    const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
    if (has('voice_of_client')) next.voice_of_client = str(p.voice_of_client);
    if (has('executive_summary')) next.executive_summary = str(p.executive_summary);
    if (has('kpi_entries')) next.kpi_entries = p.kpi_entries && typeof p.kpi_entries === 'object' ? p.kpi_entries : {};
    if (has('pillar_summaries')) next.pillar_summaries = p.pillar_summaries && typeof p.pillar_summaries === 'object' ? p.pillar_summaries : {};
    if (has('pillar1_risks')) next.pillar1_risks = arr<RiskRow>(p.pillar1_risks);
    if (has('pillar3_risks')) next.pillar3_risks = arr<RiskRow>(p.pillar3_risks);
    if (has('decisions')) next.decisions = arr<DecisionRow>(p.decisions);
    if (has('decisions_summary')) next.decisions_summary = str(p.decisions_summary);
    if (has('project_portfolio')) next.project_portfolio = arr<ProjectRow>(p.project_portfolio);
    next.updated_at = iso(now());
    reportStore()[q.key] = next;
    return ceoReport(q);
}

// ------------------------------------------------------------------ PDF

/**
 * Text to WinAnsi (the encoding the standard Helvetica fonts use), one char
 * per byte. Dashes, curly quotes and the middle dot survive; symbols the
 * fonts lack are spelled out.
 */
const WIN_ANSI: Record<string, string> = { '—': '\x97', '–': '\x96', '·': '\xb7', '•': '\x95', '‘': '\x91', '’': '\x92', '“': '\x93', '”': '\x94', '…': '\x85' };
const SPELL: [RegExp, string][] = [
    [/→/g, '->'],
    [/↑/g, 'up'],
    [/↓/g, 'down'],
    [/≥/g, '>='],
    [/≤/g, '<='],
    [/[✕×]/g, 'x'],
];

function winAnsi(s: string): string {
    let out = s;
    for (const [re, rep] of SPELL) {
        out = out.replace(re, rep);
    }
    return [...out]
        .map(ch => {
            if (WIN_ANSI[ch]) return WIN_ANSI[ch];
            const c = ch.charCodeAt(0);
            if ((c >= 0x20 && c <= 0x7e) || ch === '\n') return ch;
            if (c >= 0xa0 && c <= 0xff) return ch;
            return ch.normalize('NFKD').replace(/[^\x20-\x7e]/g, '');
        })
        .join('');
}

// Helvetica / Helvetica-Bold advance widths (1/1000 em) for 0x20..0x7e.
const W_REG = [
    278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611,
    778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
    556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];
const W_BOLD = [
    278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611,
    778, 722, 278, 556, 722, 611, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
    611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

type Font = 'F1' | 'F2' | 'F3';
type Rgb = [number, number, number];

const INK: Rgb = [0.13, 0.13, 0.13];
const MUTED: Rgb = [0.42, 0.42, 0.42];
const BRAND: Rgb = [0.706, 0.325, 0.165]; // #b4532a
const RULE: Rgb = [0.85, 0.82, 0.79];
const SHADE: Rgb = [0.957, 0.937, 0.918]; // #f4efea
const RAG_RGB: Record<string, Rgb> = { green: [0.2, 0.6, 0.36], amber: [0.93, 0.62, 0.11], red: [0.8, 0.21, 0.2], blue: [0.22, 0.47, 0.76] };

function textWidth(s: string, font: Font, size: number): number {
    const table = font === 'F2' ? W_BOLD : W_REG;
    let w = 0;
    for (const ch of s) {
        const c = ch.charCodeAt(0);
        w += c >= 0x20 && c <= 0x7e ? table[c - 0x20] : c === 0x97 ? 1000 : c === 0x96 ? 556 : c === 0xb7 || c === 0x91 || c === 0x92 ? 278 : 556;
    }
    return (w * size) / 1000;
}

/** Greedy word wrap on measured widths. Input is already WinAnsi. */
function wrapTo(text: string, font: Font, size: number, width: number): string[] {
    const out: string[] = [];
    for (const para of text.split('\n')) {
        let line = '';
        for (const word of para.split(/\s+/).filter(Boolean)) {
            const next = line ? `${line} ${word}` : word;
            if (textWidth(next, font, size) <= width || !line) {
                line = next;
            } else {
                out.push(line);
                line = word;
            }
        }
        out.push(line);
    }
    return out;
}

interface Cell {
    text?: string;
    font?: Font;
    size?: number;
    color?: Rgb;
    /** Smaller grey line(s) under the main text. */
    sub?: string;
    rag?: string;
    trend?: string;
}
interface Col {
    title: string;
    width: number;
}

/** A4 portrait, 40pt margins, Helvetica family. Coordinates are PDF points from the bottom left. */
class PdfDoc {
    readonly left = 40;
    readonly width = 515;
    private pages: string[][] = [];
    private ops: string[] = [];
    y = 0;

    constructor(private footer: string) {
        this.newPage();
    }

    newPage(): void {
        this.ops = [];
        this.pages.push(this.ops);
        this.y = 800;
    }

    ensure(h: number): void {
        if (this.y - h < 52) {
            this.newPage();
        }
    }

    private esc(s: string): string {
        return s.replace(/[\\()]/g, m => '\\' + m);
    }

    private rgb(c: Rgb): string {
        return c.map(n => n.toFixed(3)).join(' ');
    }

    text(x: number, y: number, s: string, font: Font, size: number, color: Rgb = INK): void {
        if (s) {
            this.ops.push(`BT ${this.rgb(color)} rg /${font} ${size} Tf ${x.toFixed(1)} ${y.toFixed(1)} Td (${this.esc(s)}) Tj ET`);
        }
    }

    rect(x: number, y: number, w: number, h: number, fill: Rgb): void {
        this.ops.push(`${this.rgb(fill)} rg ${x.toFixed(1)} ${y.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re f`);
    }

    rule(x1: number, y: number, x2: number, color: Rgb = RULE, w = 0.5): void {
        this.ops.push(`${this.rgb(color)} RG ${w} w ${x1.toFixed(1)} ${y.toFixed(1)} m ${x2.toFixed(1)} ${y.toFixed(1)} l S`);
    }

    triangle(x: number, y: number, up: boolean, fill: Rgb): void {
        const pts = up ? [[x, y], [x + 7, y], [x + 3.5, y + 6]] : [[x, y + 6], [x + 7, y + 6], [x + 3.5, y]];
        this.ops.push(`${this.rgb(fill)} rg ${pts.map(([a, b], i) => `${a.toFixed(1)} ${b.toFixed(1)} ${i ? 'l' : 'm'}`).join(' ')} h f`);
    }

    /** Wrapped paragraph across pages. */
    para(s: string, opts: { font?: Font; size?: number; color?: Rgb; indent?: number; after?: number } = {}): void {
        const font = opts.font ?? 'F1';
        const size = opts.size ?? 9.5;
        const lead = size * 1.38;
        const indent = opts.indent ?? 0;
        for (const p of winAnsi(s).split(/\n\s*\n/)) {
            for (const line of wrapTo(p.replace(/\n/g, ' '), font, size, this.width - indent)) {
                this.ensure(lead);
                this.y -= lead;
                this.text(this.left + indent, this.y, line, font, size, opts.color);
            }
            this.y -= opts.after ?? 4;
        }
    }

    heading(s: string): void {
        this.ensure(48);
        this.y -= 20;
        this.text(this.left, this.y, winAnsi(s), 'F2', 12.5, BRAND);
        this.y -= 5;
        this.rule(this.left, this.y, this.left + this.width, BRAND, 0.8);
        this.y -= 2;
    }

    subheading(s: string): void {
        this.ensure(34);
        this.y -= 15;
        this.text(this.left, this.y, winAnsi(s), 'F2', 10, INK);
        this.y -= 3;
    }

    table(cols: Col[], rows: Cell[][]): void {
        const size = 8.2;
        const lead = size * 1.3;
        const pad = 4;
        const header = () => {
            this.ensure(18 + lead * 2);
            this.rect(this.left, this.y - 15, this.width, 15, SHADE);
            let x = this.left;
            for (const c of cols) {
                this.text(x + pad, this.y - 10.5, winAnsi(c.title), 'F2', 7.6, MUTED);
                x += c.width;
            }
            this.y -= 15;
        };
        header();
        for (const row of rows) {
            const laid = row.map((cell, i) => {
                const w = cols[i].width - pad * 2;
                const main = cell.text ? wrapTo(winAnsi(cell.text), cell.font ?? 'F1', cell.size ?? size, w) : [];
                const sub = cell.sub ? wrapTo(winAnsi(cell.sub), 'F1', size - 1, w) : [];
                return { main, sub };
            });
            const lines = Math.max(1, ...laid.map(l => l.main.length + l.sub.length));
            const h = lines * lead + pad * 2 - 1;
            if (this.y - h < 52) {
                this.newPage();
                header();
            }
            let x = this.left;
            row.forEach((cell, i) => {
                let ty = this.y - pad - size + 0.5;
                if (cell.rag && RAG_RGB[cell.rag]) {
                    const label = cell.rag.charAt(0).toUpperCase() + cell.rag.slice(1);
                    const pw = textWidth(label, 'F2', 7) + 10;
                    this.rect(x + pad, ty - 2.5, pw, 10.5, RAG_RGB[cell.rag]);
                    this.text(x + pad + 5, ty, label, 'F2', 7, [1, 1, 1]);
                } else if (cell.trend === '↑' || cell.trend === '↓') {
                    this.triangle(x + pad, ty - 0.5, cell.trend === '↑', MUTED);
                } else {
                    for (const l of laid[i].main) {
                        this.text(x + pad, ty, l, cell.font ?? 'F1', cell.size ?? size, cell.color ?? INK);
                        ty -= lead;
                    }
                    for (const l of laid[i].sub) {
                        this.text(x + pad, ty, l, 'F1', size - 1, MUTED);
                        ty -= lead;
                    }
                }
                x += cols[i].width;
            });
            this.y -= h;
            this.rule(this.left, this.y, this.left + this.width);
        }
        this.y -= 4;
    }

    banner(title: string, subtitle: string, right: string[]): void {
        this.rect(0, 770, 595, 72, BRAND);
        this.text(this.left, 812, winAnsi(title), 'F2', 17, [1, 1, 1]);
        this.text(this.left, 794, winAnsi(subtitle), 'F1', 10.5, [1, 0.93, 0.88]);
        right.forEach((r, i) => {
            const s = winAnsi(r);
            this.text(this.left + this.width - textWidth(s, 'F1', 8.5), 812 - i * 13, s, 'F1', 8.5, [1, 0.93, 0.88]);
        });
        this.y = 756;
    }

    toBlob(): Blob {
        const objs: string[] = [
            '<< /Type /Catalog /Pages 2 0 R >>',
            '',
            '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
            '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
            '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>',
        ];
        const kids: string[] = [];
        const n = this.pages.length;
        this.pages.forEach((ops, i) => {
            const pageNo = objs.length + 1;
            kids.push(`${pageNo} 0 R`);
            const foot = winAnsi(`${this.footer} — page ${i + 1} of ${n}`);
            const footOps = [
                `${RULE.join(' ')} RG 0.5 w 40 40 m 555 40 l S`,
                `BT ${MUTED.join(' ')} rg /F1 7.5 Tf 40 29 Td (${this.esc(foot)}) Tj ET`,
                `BT ${MUTED.join(' ')} rg /F3 7.5 Tf ${(555 - textWidth('Demonstration only - all names and figures are fictional', 'F1', 7.5)).toFixed(1)} 29 Td (Demonstration only - all names and figures are fictional) Tj ET`,
            ];
            const body = [...ops, ...footOps].join('\n');
            objs.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R >> >> /Contents ${pageNo + 1} 0 R >>`);
            objs.push(`<< /Length ${body.length} >>\nstream\n${body}\nendstream`);
        });
        objs[1] = `<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${n} >>`;

        let pdf = '%PDF-1.4\n';
        const offsets: number[] = [];
        objs.forEach((o, i) => {
            offsets.push(pdf.length);
            pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
        });
        const xref = pdf.length;
        pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n` + offsets.map(o => String(o).padStart(10, '0') + ' 00000 n \n').join('');
        pdf += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
        // Every char is <= 0xff (winAnsi), so one char is one byte and the offsets hold.
        const bytes = new Uint8Array(pdf.length);
        for (let i = 0; i < pdf.length; i++) {
            bytes[i] = pdf.charCodeAt(i) & 0xff;
        }
        return new Blob([bytes], { type: 'application/pdf' });
    }
}

const fmtDate = (d: string): string => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

/** GET /api/quarterly-report/:quarter/pdf — the report as a board-pack PDF. */
export function ceoReportPdf(q: Quarter): { blob: Blob; filename: string } {
    const report = ceoReport(q) as any;
    const doc = new PdfDoc(`${ORG.name} · Quarterly CEO Report · ${q.label}`);
    const isCurrent = q.key === currentQuarter().key;

    doc.banner(ORG.name, `Quarterly CEO Report — ${q.label}`, [
        `${fmtDate(report.period.start)} – ${fmtDate(report.period.end)}${isCurrent ? ' (quarter to date)' : ''}`,
        report.updated_at ? `Last saved ${fmtDate(report.updated_at)}` : 'Draft — not yet saved',
        `Prepared by ${LEADERSHIP.ceo.name}, ${LEADERSHIP.ceo.title}`,
    ]);

    // Scorecard: RAG counts per pillar.
    doc.heading('Scorecard at a glance');
    doc.table(
        [
            { title: 'Pillar', width: 215 },
            { title: 'Green', width: 60 },
            { title: 'Amber', width: 60 },
            { title: 'Red', width: 60 },
            { title: 'Headline', width: 120 },
        ],
        report.pillars.map((p: any) => {
            const count = (c: string) => p.kpis.filter((k: any) => k.rag === c).length;
            const worst = p.kpis.find((k: any) => k.rag === 'red') ?? p.kpis.find((k: any) => k.rag === 'amber');
            return [
                { text: p.name, font: 'F2' as Font },
                { text: String(count('green')), color: RAG_RGB['green'], font: 'F2' as Font },
                { text: String(count('amber')), color: RAG_RGB['amber'], font: 'F2' as Font },
                { text: String(count('red')), color: count('red') ? RAG_RGB['red'] : MUTED, font: 'F2' as Font },
                { text: worst ? `Watch: ${worst.label.replace(/ %$/, '')}` : 'All on track', size: 7.6, color: MUTED },
            ];
        }),
    );

    doc.heading('Executive summary');
    doc.para(report.executive_summary || 'Not yet written.');
    if (report.voice_of_client) {
        doc.subheading('Voice of a client — from the heart');
        doc.para(report.voice_of_client, { font: 'F3', color: MUTED });
    }

    const riskTable = (title: string, rows: RiskRow[]) => {
        if (!rows.length) return;
        doc.subheading(title);
        doc.table(
            [
                { title: 'Risk', width: 175 },
                { title: 'Rating', width: 80 },
                { title: 'Controls', width: 130 },
                { title: 'Action / owner', width: 130 },
            ],
            rows.map(r => [
                { text: r.risk, font: 'F2' as Font, sub: r.cause ? `Cause: ${r.cause}` : undefined },
                { text: [r.likelihood, r.consequence].filter(Boolean).join(' / ') },
                { text: r.controls },
                { text: r.action },
            ]),
        );
    };

    report.pillars.forEach((pillar: any, idx: number) => {
        doc.heading(pillar.name);
        if (pillar.summary) {
            doc.para(pillar.summary, { after: 6 });
        }
        doc.table(
            [
                { title: 'KPI', width: 200 },
                { title: 'Result', width: 120 },
                { title: 'Target', width: 95 },
                { title: 'Trend', width: 50 },
                { title: 'RAG', width: 50 },
            ],
            pillar.kpis.map((k: any) => [
                // The source note carries the as-at marker the page shows
                // ("as at 30 Sep 2025", "(current)"), so a past quarter's PDF
                // never passes today's point-in-time figures off as its own.
                { text: k.label, sub: [k.source_note, k.notes].filter(Boolean).join('\n') || undefined },
                { text: k.result || '—', font: 'F2' as Font },
                { text: k.target || '—', color: MUTED },
                k.trend === '↑' || k.trend === '↓' ? { trend: k.trend } : { text: k.trend || '', color: MUTED },
                k.rag ? { rag: k.rag } : { text: '—', color: MUTED },
            ]),
        );
        if (idx === 0) riskTable('Key risks & issues', report.pillar1_risks);
        if (idx === 2) riskTable('Financial risks & mitigations', report.pillar3_risks);
    });

    doc.heading('Decisions required — Board / CEO');
    if (report.decisions_summary) doc.para(report.decisions_summary, { after: 6 });
    if (report.decisions.length) {
        doc.table(
            [
                { title: 'Decision', width: 185 },
                { title: 'Rationale', width: 125 },
                { title: 'If deferred', width: 100 },
                { title: 'Recommendation', width: 105 },
            ],
            (report.decisions as DecisionRow[]).map(d => [{ text: d.decision, font: 'F2' as Font }, { text: d.rationale }, { text: d.impact_if_deferred }, { text: d.recommendation }]),
        );
    } else {
        doc.para('No decisions required this quarter.');
    }

    doc.heading('Project portfolio');
    if (report.project_portfolio.length) {
        doc.table(
            [
                { title: 'Initiative', width: 200 },
                { title: 'Pillar', width: 95 },
                { title: 'Due', width: 55 },
                { title: 'Status', width: 55 },
                { title: 'Owner', width: 110 },
            ],
            (report.project_portfolio as ProjectRow[]).map(p => [
                { text: p.initiative, font: 'F2' as Font, sub: p.benefits || undefined },
                { text: (p.pillar || '—').replace(/^Pillar \d — /, '') },
                { text: p.milestone_due || 'TBC' },
                p.status ? { rag: p.status } : { text: '—', color: MUTED },
                { text: p.owner || 'TBC' },
            ]),
        );
    } else {
        doc.para('No projects recorded.');
    }

    return { blob: doc.toBlob(), filename: `NPY-Quarterly-CEO-Report-${q.key}.pdf` };
}

// =============================================================== AI assistant

type Topic = 'overview' | 'referrals' | 'incidents' | 'medications' | 'goals' | 'whs' | 'compliance' | 'training' | 'payroll' | 'budget' | 'finance' | 'occupancy' | 'people';

/** Checked in order; a question can match several (answered together, up to three). */
const TOPICS: [Topic, RegExp][] = [
    ['overview', /\b(overview|headlines?|how are we (going|tracking|doing)|board (summary|update|paragraph)|summar(y|ise|ize) (for the board|everything|the organisation|our kpis)|executive summary|all (the )?kpis)\b/],
    ['referrals', /referr|pipeline|intake|wait ?list/],
    ['incidents', /incident|reportable|choking/],
    ['medications', /medic|\bmeds?\b|\bdoses?\b|refus/],
    ['goals', /\bgoals?\b|care plan|plan reviews?|review compliance/],
    ['whs', /\bwhs\b|audit|inspection|tendable|hazard|health and safety|health & safety/],
    ['compliance', /complian|expired|missing|clearance|screening|police|working with children|certif|licen[cs]e/],
    ['training', /training|course|etrainu|induction|first aid|\bcpr\b/],
    ['payroll', /payroll|overtime|superannuation|\bsuper\b|wages?|gross pay|pay ?runs?|penalt|allowance/],
    ['budget', /budget|variance/],
    ['finance', /financ|revenue|income|margin|profit|expens|spend|cash|receivable|debtor|\bar\b|invoice|labour cost|surplus|deficit|money/],
    ['occupancy', /occupan|\bsil\b|\bbeds?\b|\bhouses?\b|respite|supported living|accommodation/],
    ['people', /staff|turnover|head ?count|employee|workforce|attrition|\bhires?\b|starters?|leavers?|recruit|vacanc|\bfte\b|aboriginal|\batsi\b|anangu|sick leave|people/],
];

const TOOL: Record<Topic, string[]> = {
    overview: ['incidents_summary', 'compliance_summary', 'people_payroll', 'finance_kpis', 'occupancy_summary'],
    referrals: ['referral_summary'],
    incidents: ['incidents_summary'],
    medications: ['medications_summary'],
    goals: ['care_plan_reviews'],
    whs: ['whs_audits'],
    compliance: ['compliance_summary'],
    training: ['compliance_summary'],
    payroll: ['people_payroll'],
    budget: ['finance_kpis'],
    finance: ['finance_kpis'],
    occupancy: ['occupancy_summary'],
    people: ['people_payroll'],
};

/** Questions we know the data can't answer, with the reason Assistant Insights shows. */
const UNANSWERABLE: [RegExp, string][] = [
    [/ngangkari.*(session|treatment|healing|visit)s?|(session|treatment|healing)s?.*ngangkari/, 'Ngangkari session counts are not recorded in any system the dashboard reads.'],
    [/(basket|artwork|weaving|sculpture)s?.*(sold|sales)|(sold|sales).*(basket|artwork|weaving)/, 'Tjanpi retail sales by item are not in the finance data — only general-ledger totals.'],
    [/fleet|kilomet|\bkms?\b|vehicle (use|logs?)|fuel (use|usage)/, 'Vehicle and fleet logs are not connected to the dashboard.'],
    [/survey|satisfaction|feedback score|nps\b/, 'Client survey results are not imported into the dashboard.'],
    [/meals?\b|lunches|breakfasts/, 'Child Nutrition meal counts are kept in a separate spreadsheet that is not connected.'],
    [/weather|forecast for|road (closure|condition)s?/, 'Weather and road conditions are outside the dashboard data.'],
    [/who (is|was) (the )?(most|least)|which (staff member|employee|worker) (has|had|is|was) (the )?(most|least|best|worst)/, 'The assistant only reports aggregate figures, not rankings of individual staff.'],
];

/** "last quarter", "Q1 FY27", "2027-Q1", "all time" — else the current quarter. */
function quarterFrom(text: string): { q: Quarter | null; explicit: boolean } {
    const iso1 = /\b(20\d{2})-q([1-4])\b/i.exec(text);
    if (iso1) {
        return { q: quarter(`${iso1[1]}-Q${iso1[2]}`), explicit: true };
    }
    const fy = /\bq([1-4])\s*(?:fy\s*)?'?(\d{2}|20\d{2})\b/i.exec(text);
    if (fy) {
        const year = fy[2].length === 2 ? 2000 + Number(fy[2]) : Number(fy[2]);
        return { q: quarter(`${year}-Q${fy[1]}`), explicit: true };
    }
    if (/\b(last|previous|prior) quarter\b/i.test(text)) {
        const qs = allQuarters();
        return { q: qs[1] ?? qs[0], explicit: true };
    }
    if (/\ball[- ]time\b|\bsince (dec|december)\b|\bever\b/i.test(text)) {
        return { q: null, explicit: true };
    }
    return { q: currentQuarter(), explicit: false };
}

function topicsIn(text: string): Topic[] {
    const t = text.toLowerCase();
    const found = TOPICS.filter(([, re]) => re.test(t)).map(([k]) => k);
    // "How are we tracking against budget?" is a budget question; the overview
    // is only for questions that name nothing more specific.
    if (found.includes('overview')) {
        const specific = found.filter(k => k !== 'overview');
        if (!specific.length) {
            return ['overview'];
        }
        found.splice(found.indexOf('overview'), 1);
    }
    const drop = new Set<Topic>();
    // "staff ... compliance" is a compliance question, not a headcount one.
    if (found.some(k => k === 'compliance' || k === 'training' || k === 'payroll')) drop.add('people');
    // "medication compliance" / "review compliance" are client measures, not workforce compliance.
    if ((found.includes('medications') || found.includes('goals')) && !/staff|worker|employee|workforce|training|clearance|screening/.test(t)) drop.add('compliance');
    // "SIL/HCS referral" is about the pipeline, not the houses.
    if (found.includes('referrals') && !/occupan|\bbeds?\b|vacan/.test(t)) drop.add('occupancy');
    if (found.includes('budget')) drop.add('finance');
    // "What did we spend on payroll?" is a payroll question unless it also names a finance measure.
    if (found.includes('payroll') && !/revenue|income|margin|profit|receivable|debtor|invoice|labour cost|surplus|deficit/.test(t)) drop.add('finance');
    return found.filter(k => !drop.has(k)).slice(0, 3);
}

// ---------------------------------------------------------------- sections

function incidentsAnswer(q: Quarter | null): string {
    const s = incStats(q);
    const rep = incidentReportable(q);
    const label = scopeLabel(q);
    if (!s.total) {
        return `No incidents have been recorded for ${label}.`;
    }
    const under14 = Math.max(0, s.open - s.overdue_14 - s.overdue_21 - s.overdue_28);
    const openBySite = tally(
        incidentsIn(scopeOf(q)).filter(i => i.open),
        i => i.location,
    ).slice(0, 5);
    const parts = [
        `**${s.open} of ${s.total} incidents are open** for ${label}, and **${s.overdue_28} ${s.overdue_28 === 1 ? 'is' : 'are'} overdue** (open 28 days or more).`,
        table(
            ['Open incident age', 'Count'],
            [
                ['Under 14 days', under14],
                ['14–20 days', s.overdue_14],
                ['21–27 days', s.overdue_21],
                ['28 days or more', s.overdue_28],
            ],
        ),
        `The closure rate is **${s.closure_rate}%**. ${rep.count ? `${plural(rep.count, 'incident')} ${rep.count === 1 ? 'was' : 'were'} reportable (${Object.entries(rep.byLevel)
            .map(([l, c]) => `${l}: ${c}`)
            .join(', ')})` : 'None were reportable'}${s.choking ? `, and ${plural(s.choking, 'involved', 'involved')} choking` : ''}.`,
    ];
    if (openBySite.length) {
        parts.push('Open incidents by community:', table(['Community', 'Open'], openBySite.map(([site, n]) => [site, n])));
    }
    return parts.join('\n\n');
}

function medicationsAnswer(q: Quarter | null): string {
    const m = medStats(q);
    const owed = m.total - m.excluded;
    const parts = [
        `Medication compliance is **${m.compliance_rate}%** for ${scopeLabel(q)}: ${num(m.given)} of ${num(owed)} doses owed were given or self-administered.`,
        table(
            ['Dose outcome', 'Count'],
            [
                ['Given / self-administered', num(m.given)],
                ['Refused', num(m.refused)],
                ['Missed (not received or medication issue)', num(m.missed)],
                ['Needs review (unclassified)', num(m.needs_review)],
                ['Excluded (not required / client unavailable)', num(m.excluded)],
            ],
        ),
    ];
    const refused = m.refused_by_medicine.slice(0, 3);
    if (refused.length) {
        parts.push(`Most refused: ${refused.map(r => `${r.medicine_name} (${r.count})`).join(', ')}.`);
    }
    if (m.needs_review) {
        parts.push(`${plural(m.needs_review, 'record')} still need a reason code before they count either way.`);
    }
    return parts.join('\n\n');
}

function goalsAnswer(q: Quarter | null): string {
    const g = golStats(q);
    const gp = goalProgression(q);
    const parts = [
        `Care plan review compliance is **${g.review_compliance_rate}%** for ${scopeLabel(q)} (goal reviews signed off within ten days of falling due).`,
        table(
            ['Measure', 'Count'],
            [
                ['Goals in scope', g.total],
                ['Active goals', g.active],
                ['Reviewed in period', g.reviewed],
                ['Overdue reviews', g.overdue_reviews],
                ['Active goals with no review date', g.no_review_date],
            ],
        ),
    ];
    if (gp.reviewed) {
        parts.push(`Of the ${gp.reviewed} goals reviewed, **${gp.achievedPct}% were achieved** and ${gp.partialPct}% partly achieved.`);
    }
    return parts.join('\n\n');
}

function whsAnswer(q: Quarter | null): string {
    const w = whsAuditSummary(q);
    const t = tendableStats(q, 'WHS') as unknown as TendableStats;
    const all = tendableStats(q) as unknown as TendableStats;
    const parts = [
        w.audits
            ? `**${plural(w.audits, 'WHS audit')}** were completed for ${scopeLabel(q)}, with an average score of **${w.averageScore}%**.`
            : `No WHS audits have been completed yet for ${scopeLabel(q)}.`,
    ];
    if (all.by_type.length) {
        parts.push(table(['Audit type', 'Completed', 'Avg score'], all.by_type.map(b => [b.type, b.count, b.averageScore === null ? '–' : `${b.averageScore}%`])));
    }
    const weakest = t.category_averages.filter(c => c.averageScore !== null && c.averageScore < 95).slice(0, 3);
    if (weakest.length) {
        parts.push(`WHS categories needing attention: ${weakest.map(c => `${c.category} (${c.averageScore}%)`).join(', ')}.`);
    } else if (w.audits) {
        parts.push('Every WHS category averaged 95% or better.');
    }
    parts.push(`${plural(all.actions.open, 'corrective action')} ${all.actions.open === 1 ? 'is' : 'are'} open, ${all.actions.overdue} overdue; ${all.actions.completed} completed.`);
    return parts.join('\n\n');
}

function complianceAnswer(): string {
    const c = complianceSummary();
    const gaps = staffWithGaps();
    const ndis = c.ndis_worker_screening;
    const workforce = gaps.cats.find(cat => !/training/i.test(cat.name));
    const workforceStaff = workforce ? gaps.byCat.get(workforce.name)?.size ?? 0 : 0;
    const parts = [
        `Overall compliance is **${c.overall_rate}%** (${num(c.complete)} of ${num(c.total_checks)} checks across ${gaps.staff} active staff).` +
            (workforce
                ? ` **${plural(workforceStaff, 'staff member')}** ${workforceStaff === 1 ? 'has' : 'have'} an expired or missing workforce check (clearances, licences, certificates); most other gaps are training modules still being completed.`
                : ''),
        table(
            ['Category', 'Rate', 'Staff affected', 'Expired', 'Missing'],
            gaps.cats.map(cat => [
                cat.name,
                `${cat.rate}%`,
                gaps.byCat.get(cat.name)?.size ?? 0,
                cat.items.reduce((n, i) => n + i.expired, 0),
                cat.items.reduce((n, i) => n + i.missing, 0),
            ]),
        ),
        ndis.expired + ndis.missing
            ? `**NDIS Worker Screening Clearance:** ${ndis.missing} missing · ${ndis.expired} expired (${ndis.rate}% compliant). Those staff are rostered off NDIS shifts until cleared.`
            : 'Every NDIS-facing staff member holds a current NDIS Worker Screening Clearance.',
    ];
    const below = [...c.items_below_target].sort((a, b) => a.rate - b.rate).slice(0, 4);
    if (below.length) {
        parts.push(`Below target: ${below.map(b => `${b.name} (${b.rate}%)`).join(', ')}.`);
    }
    return parts.join('\n\n');
}

function trainingAnswer(): string {
    const t = trainingSummary();
    const c = complianceSummary();
    const courses = t.by_course
        .map(b => ({ name: b.courseName, pct: b.total ? Math.round((b.complete / b.total) * 100) : 0, total: b.total }))
        .sort((a, b) => a.pct - b.pct)
        .slice(0, 3);
    const parts = [
        `Training completion is **${t.completion_rate}%** across ${num(t.total)} enrolments for ${t.staff_count} staff, with **${t.in_progress} in progress**.`,
        table(
            ['Measure', 'Value'],
            [
                ['Mandatory training compliance', `${c.mandatory_rate}%`],
                ['Essential training compliance', `${c.essential_rate}%`],
                ['Certificates expiring in 90 days', t.expiring_soon],
                ['Certificates expired', t.expired],
                ['Staff not yet set up in eTrainu', t.untracked_staff.length],
            ],
        ),
    ];
    if (courses.length) {
        parts.push(`Lowest completion: ${courses.map(x => `${x.name} (${x.pct}% of ${x.total})`).join(', ')}.`);
    }
    return parts.join('\n\n');
}

function peopleAnswer(q: Quarter | null, text = ''): string {
    const p = peopleSummary(q);
    const period = q ? `Q${q.q} FY${String(q.fy).slice(2)}${q.key === currentQuarter().key ? ' to date' : ''}` : 'since Dec 2025';
    const reasons = [...p.termination_reasons_in_period].sort((a, b) => b.count - a.count).slice(0, 3);
    const flows = /hire|starter|leaver|left|exit|resign/i.test(text);
    // A closed quarter also gets its own end-of-quarter figures, exactly as the
    // CEO report quotes them (same 12-month rolling definition as today's).
    const closed = q && q.key !== currentQuarter().key ? q : null;
    const endLabel = closed ? dayLabel(closed.end) : '';
    const end = p.turnover_at_period_end;
    const asAtEnd = closed ? ` (${end.rate}% as at ${endLabel})` : '';
    const parts = [
        flows
            ? `**${plural(p.hires_in_period, 'staff member')} started and ${p.terminations_in_period} left** in ${scopeLabel(q)}. We have ${p.active_staff} active staff (${p.total_fte} FTE) today and 12-month rolling turnover is **${p.turnover_rate_12m}%**${asAtEnd}.`
            : `We have **${p.active_staff} active staff** (${p.total_fte} FTE) today and 12-month rolling turnover is **${p.turnover_rate_12m}%**${asAtEnd}.`,
        table(
            ['Measure', 'Value'],
            [
                ...(closed
                    ? [
                          [`Head count at ${endLabel}`, `${p.headcount_at_period_end} (${p.headcount_at_period_end_by_type.fulltime} / ${p.headcount_at_period_end_by_type.parttime} / ${p.headcount_at_period_end_by_type.casual} FT / PT / casual)`],
                          [`12-month turnover at ${endLabel}`, `${end.rate}% (${end.leavers} leavers ÷ ${end.avg_headcount} average headcount)`],
                          [`Aboriginal & Torres Strait Islander staff at ${endLabel}`, `${p.atsi_at_period_end.atsi_percent}%`],
                      ]
                    : []),
                ['Full time / part time / casual today', `${p.headcount_by_type.fulltime} / ${p.headcount_by_type.parttime} / ${p.headcount_by_type.casual}`],
                [`Starters (${period})`, p.hires_in_period],
                [`Leavers (${period})`, p.terminations_in_period],
                [`Aboriginal & Torres Strait Islander staff${closed ? ' today' : ''}`, `${p.atsi_percent}%`],
                [`Open roles${closed ? ' today' : ''}`, `${p.recruitment.open} (${p.recruitment.overdue} past the ${p.recruitment.target_days}-day target)`],
            ],
        ),
    ];
    if (reasons.length) {
        parts.push(`Main reasons for leaving: ${reasons.map(r => `${r.reason} (${r.count})`).join(', ')}.`);
    }
    if (p.recruitment.avg_time_to_fill_days !== null) {
        parts.push(`Average time to fill is ${p.recruitment.avg_time_to_fill_days} days.`);
    }
    return parts.join('\n\n');
}

function payrollAnswer(q: Quarter | null): string {
    const p = peopleSummary(q).payroll_in_period;
    if (!p.months.length) {
        return `No pay runs have been processed yet for ${scopeLabel(q)}.`;
    }
    const share = (v: number) => (p.gross > 0 ? `${round1((v / p.gross) * 100)}%` : '–');
    // The finance ledger over exactly the pay-run months: its wage accounts are
    // the labour cost ratio's numerator (finance page, finance answer).
    const ledger = ledgerWages(p.months[0], p.months[p.months.length - 1]);
    const revenue = p.months
        .map(monthTotals)
        .filter((m): m is MonthTotals => !!m)
        .reduce((n, m) => n + m.revenue, 0);
    const ratio = revenue > 0 ? round1((ledger.wages / revenue) * 100) : 0;
    const reconciles = Math.abs(ledger.wages - p.gross) <= 1 && Math.abs(ledger.superannuation - p.super) <= 1;
    return [
        `Gross pay for ${scopeLabel(q)} is **${money(p.gross)}** across ${plural(p.pay_runs, 'pay run')}.`,
        table(
            ['Component', 'Amount', 'Share of gross'],
            [
                ['Overtime', money(p.overtime), share(p.overtime)],
                ['Penalty rates', money(p.penalty), share(p.penalty)],
                ['Allowances (incl. remote)', money(p.allowance), share(p.allowance)],
                ['Casual wages', money(p.casual), share(p.casual)],
                ['Annual leave', money(p.annual_leave), share(p.annual_leave)],
                ['Superannuation', money(p.super), share(p.super)],
            ],
        ),
        reconciles
            ? `This reconciles to the finance ledger for the same months: wages ${money(ledger.wages)} (${ratio}% of revenue, the labour cost ratio) and superannuation ${money(ledger.superannuation)}.`
            : `The finance ledger for the same months shows wages of ${money(ledger.wages)} (${ratio}% of revenue, the labour cost ratio) and superannuation of ${money(ledger.superannuation)}.`,
    ].join('\n\n');
}

function financeAnswer(q: Quarter | null, fyAsked = false): string {
    const ytd = financeYtd();
    const p = fyAsked || !q ? ytd : profitability(q);
    // FY to date as at the end of the asked quarter: the CEO report's figure.
    const fytd = q && !fyAsked ? marginFyToDate(q) : null;
    const scope = fyAsked || !q ? `FY ${fyLabel(currentFy())} to date` : scopeLabel(q);
    const ar = accountsReceivable();
    const over90 = ar.aging.over_90;
    const parts = [
        `For ${scope}, revenue is **${money(p.revenue_ytd)}** against expenses of ${money(p.expenses_ytd)} — an operating margin of **${p.operating_margin_percent}%**.`,
        table(
            ['Measure', 'Value'],
            [
                fyAsked || !q
                    ? ['Operating margin, this quarter', `${profitability(currentQuarter()).operating_margin_percent}% (revenue ${money(profitability(currentQuarter()).revenue_ytd)})`]
                    : [`Operating margin, ${fytd!.scope}`, `${fytd!.margin}% (revenue ${money(fytd!.revenue)})`],
                ['Labour cost', `${money(p.labour_cost_ytd)} (${p.labour_cost_ratio_percent}% of revenue)`],
                ['Accounts receivable', `${money(ar.total_outstanding)} across ${ar.open_invoices} invoices`],
                ['Average days outstanding', `${ar.avg_days_outstanding} days`],
                ['Over 90 days', money(over90)],
            ],
        ),
    ];
    if (ar.top_debtors.length) {
        parts.push(`Largest debtor: ${ar.top_debtors[0].customer} (${money(ar.top_debtors[0].outstanding)}).`);
    }
    return parts.join('\n\n');
}

function budgetAnswer(): string {
    const b = budgetYtd();
    const ytd = financeYtd();
    const fy = fyLabel(currentFy());
    if (!b) {
        return `No budget has been loaded for ${fy} yet, so I can only report actuals: revenue ${money(ytd.revenue_ytd)}, margin ${ytd.operating_margin_percent}% FY to date.`;
    }
    const revVar = ytd.revenue_ytd - b.revenue;
    const expVar = ytd.expenses_ytd - b.expenses;
    return [
        `FY ${fy} to date, the operating margin is **${ytd.operating_margin_percent}%** against a budgeted **${b.margin}%**.`,
        table(
            ['', 'Actual', 'Budget', 'Variance'],
            [
                ['Revenue', money(ytd.revenue_ytd), money(b.revenue), `${revVar >= 0 ? '+' : ''}${money(revVar)}`],
                ['Expenses', money(ytd.expenses_ytd), money(b.expenses), `${expVar >= 0 ? '+' : ''}${money(expVar)}`],
            ],
        ),
        `The current month is compared pro rata. Line-by-line detail is on **Finance › Budgets**.`,
    ].join('\n\n');
}

function occupancyAnswer(): string {
    const h = houses();
    return [
        `Supported accommodation and respite occupancy is **${h.totals.rate}%** — ${h.totals.occupied} of ${h.totals.capacity} beds across ${h.totals.residential_facility_count} houses, with **${plural(h.vacant, 'vacant bed')}**.`,
        table(
            ['House', 'Beds', 'Occupied', 'Occupancy'],
            h.rows.map(f => [f.name, f.capacity ?? 0, f.occupied, `${f.rate}%`]),
        ),
    ].join('\n\n');
}

/**
 * Where a referral sits in the pipeline, as ONE stage: the organisation's
 * decision first, and accepted referrals by their placement outcome, so the
 * stages are mutually exclusive and add up to the total.
 */
function referralStage(r: { companyDecision: string | null; outcomeStatus: string | null }): string {
    switch (r.companyDecision) {
        case null:
        case '':
            return 'Awaiting decision';
        case 'Accept':
            return r.outcomeStatus === 'Active Placement'
                ? 'Accepted – active placement'
                : r.outcomeStatus === 'Pending Placement'
                  ? 'Accepted – pending placement'
                  : r.outcomeStatus === 'Did Not Proceed'
                    ? 'Accepted – did not proceed'
                    : r.outcomeStatus === 'Referred Elsewhere'
                      ? 'Accepted – referred elsewhere'
                      : 'Accepted – placement not yet arranged';
        case 'Decline':
            return 'Declined';
        case 'Waitlist':
            return 'Waitlisted';
        case 'Further Assessment Required':
            return 'Further assessment required';
        case 'Pending':
            return 'Decision pending';
        default:
            return r.companyDecision;
    }
}

const STAGE_ORDER = [
    'Awaiting decision',
    'Decision pending',
    'Further assessment required',
    'Waitlisted',
    'Accepted – placement not yet arranged',
    'Accepted – pending placement',
    'Accepted – active placement',
    'Accepted – did not proceed',
    'Accepted – referred elsewhere',
    'Declined',
];

function referralsAnswer(q: Quarter | null): string {
    const live = referrals().filter(r => !r.archived);
    const s = scopeOf(q);
    const inPeriod = live.filter(r => new Date(r.dateReceived) >= s.from && new Date(r.dateReceived) <= s.to);
    const awaiting = live.filter(r => !r.companyDecision);
    const oldest = awaiting.map(r => Math.floor((now().getTime() - new Date(r.dateReceived).getTime()) / 86400000)).sort((a, b) => b - a)[0];
    const stageRank = (k: string) => (STAGE_ORDER.indexOf(k) < 0 ? STAGE_ORDER.length : STAGE_ORDER.indexOf(k));
    const stages = tally(live, referralStage).sort((a, b) => stageRank(a[0]) - stageRank(b[0]) || a[0].localeCompare(b[0]));
    const types = tally(live, r => r.silHcsType || 'Not recorded').sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const parts = [
        `There are **${live.length} referrals** in the pipeline; **${awaiting.length} are awaiting a decision**${oldest !== undefined ? ` (the oldest received ${oldest} days ago)` : ''}. ${plural(inPeriod.length, 'referral')} ${inPeriod.length === 1 ? 'was' : 'were'} received in ${scopeLabel(q)}.`,
        table(['Stage', 'Count'], [...stages.map(([k, n]) => [k, n] as [string, number]), ['**Total**', `**${live.length}**`]]),
        table(['Support type', 'Count'], [...types.map(([k, n]) => [k, n] as [string, number]), ['**Total**', `**${live.length}**`]]),
    ];
    return parts.join('\n\n');
}

/** "operating margin 1.9% on revenue of $2.13M quarter to date (2.9% on $12.61M FY to date)". */
function financeHeadline(q: Quarter | null): string {
    const fy = financeYtd();
    if (!q) {
        return `operating margin ${fy.operating_margin_percent}% on revenue of ${money(fy.revenue_ytd)} FY to date`;
    }
    const p = profitability(q);
    const scope = q.key === currentQuarter().key ? 'quarter to date' : `in ${q.label}`;
    const fytd = marginFyToDate(q);
    const head = `operating margin ${p.operating_margin_percent}% on revenue of ${money(p.revenue_ytd)} ${scope}`;
    // In Q1 the FY to date is the quarter itself.
    return q.q === 1 ? head : `${head}, and ${fytd.margin}% on ${money(fytd.revenue)} for ${fytd.scope}`;
}

function overviewAnswer(q: Quarter | null): string {
    const b = boardDashboard(q) as any;
    const c = complianceSummary();
    const h = houses();
    const inc = incStats(q);
    const ndis = c.ndis_worker_screening.expired + c.ndis_worker_screening.missing;
    return [
        `Here are the headlines for ${scopeLabel(q)}:`,
        [
            `- **Quality & client care:** ${b.quality.incidents} incidents (${b.quality.open_incidents} open, ${inc.overdue_28} overdue); medication compliance ${b.quality.medication_compliance_rate}%; care plan review compliance ${b.quality.care_plan_review_rate}%.`,
            `- **People${q && q.key !== currentQuarter().key ? ' (as at today)' : ''}:** ${b.people.active_staff} active staff; turnover ${b.people.turnover_rate_12m}% (12m); training completion ${b.people.training_completion_rate}%${ndis ? `; ${plural(ndis, 'staff member')} missing or expired NDIS Worker Screening` : ''}.`,
            `- **Finance:** ${financeHeadline(q)}; ${money(b.finance.outstanding_ar)} receivable across ${b.finance.open_invoices} invoices${q && q.key !== currentQuarter().key ? ' today' : ''}.`,
            `- **Supported accommodation & respite:** ${h.totals.rate}% occupancy (${plural(h.vacant, 'vacant bed')}).`,
        ].join('\n'),
        'Ask me about any of these for more detail.',
    ].join('\n\n');
}

function section(topic: Topic, q: Quarter | null, text = ''): string {
    switch (topic) {
        case 'overview':
            return overviewAnswer(q);
        case 'referrals':
            return referralsAnswer(q);
        case 'incidents':
            return incidentsAnswer(q);
        case 'medications':
            return medicationsAnswer(q);
        case 'goals':
            return goalsAnswer(q);
        case 'whs':
            return whsAnswer(q);
        case 'compliance':
            return complianceAnswer();
        case 'training':
            return trainingAnswer();
        case 'payroll':
            return payrollAnswer(q);
        case 'budget':
            return budgetAnswer();
        case 'finance':
            return financeAnswer(q, /financial year|this year|\bfy\b|\bytd\b|year to date|annual/i.test(text));
        case 'occupancy':
            return occupancyAnswer();
        case 'people':
            return peopleAnswer(q, text);
    }
}

const SECTION_TITLE: Record<Topic, string> = {
    overview: 'Overview',
    referrals: 'Referrals',
    incidents: 'Incidents',
    medications: 'Medications',
    goals: 'Care plan reviews',
    whs: 'WHS audits',
    compliance: 'Workforce compliance',
    training: 'Training',
    payroll: 'Payroll',
    budget: 'Budget',
    finance: 'Finance',
    occupancy: 'Supported accommodation & respite',
    people: 'Workforce',
};

const CAPABILITIES = [
    '- **Incidents** — open, overdue, reportable, by community',
    '- **Medications** — compliance, refusals, missed doses',
    '- **Care plan reviews** — review compliance, overdue reviews, goal outcomes',
    '- **WHS audits** — audits completed, scores, open actions',
    '- **Workforce** — headcount, turnover, starters and leavers, recruitment',
    '- **Compliance & training** — expired or missing checks, NDIS Worker Screening, course completion',
    '- **Payroll** — gross pay, overtime, penalties, super',
    '- **Finance** — revenue, margin, budget vs actual, receivables',
    '- **Supported accommodation & respite** — occupancy by house',
    '- **Referrals** — the intake pipeline',
].join('\n');

interface AssistantReply {
    answer: string;
    tools: string[];
    reason?: string;
}

/** The canned "model": keyword routing over the same generators the pages use. */
export function assistantReply(question: string, history: { role: string; content: string }[] = []): AssistantReply {
    const text = question.trim();
    const lower = text.toLowerCase();

    if (/^(hi|hello|hey|g'?day|good (morning|afternoon|evening)|thanks|thank you|cheers)\b[\s!.,]*$/i.test(text)) {
        const thanks = /thank|cheers/i.test(text);
        return {
            answer: `${thanks ? "You're welcome!" : 'Hi! I can answer questions about the dashboard data.'} Here's what I can help with:\n\n${CAPABILITIES}`,
            tools: [],
        };
    }

    for (const [re, reason] of UNANSWERABLE) {
        if (re.test(lower)) {
            return {
                answer: `I can't answer that from the dashboard data. ${reason}\n\nHere's what I can help with:\n\n${CAPABILITIES}`,
                tools: [],
                reason,
            };
        }
    }

    let { q, explicit } = quarterFrom(text);
    let topics = topicsIn(text);
    if (!topics.length) {
        // A follow-up such as "and last quarter?" carries on the previous topic.
        const prior = [...history].reverse().find(h => h.role === 'user' && topicsIn(h.content).length);
        if (prior && (explicit || /^(and|what about|how about|same|compare)/i.test(text))) {
            topics = topicsIn(prior.content);
            if (!explicit) {
                q = quarterFrom(prior.content).q;
            }
        }
    }

    if (!topics.length) {
        const reason = 'The question did not match any data source the assistant can query.';
        return {
            answer: `That's outside the dashboard data, so I can't answer it. Here's what I can help with:\n\n${CAPABILITIES}\n\nTry asking, for example, *"How is staff turnover tracking?"* or *"What is our operating margin this financial year?"*`,
            tools: [],
            reason,
        };
    }

    const body =
        topics.length === 1
            ? section(topics[0], q, text)
            : topics.map(t => `### ${SECTION_TITLE[t]}\n\n${section(t, q, text)}`).join('\n\n');
    const tools = [...new Set(topics.flatMap(t => TOOL[t]))];
    return { answer: body, tools };
}

function chatMessage(ctx: MockContext): unknown {
    const question = String(ctx.body?.question ?? '').trim();
    if (!question) {
        return respond({ error: 'A question is required.' }, 400);
    }
    const history = Array.isArray(ctx.body?.history) ? (ctx.body.history as { role: string; content: string }[]).filter(h => h && typeof h.content === 'string') : [];
    const reply = assistantReply(question.slice(0, 2000), history);
    const historyChars = history.reduce((n, h) => n + h.content.length, 0);
    return {
        answer: reply.answer,
        citations: [],
        toolCalls: reply.tools.map(tool => ({ tool, error: false })),
        usage: {
            inputTokens: 3900 + Math.round((question.length + historyChars) / 4) + reply.tools.length * 420,
            outputTokens: Math.max(12, Math.round(reply.answer.length / 4)),
            cacheReadInputTokens: history.length ? 3600 : 0,
            cacheCreationInputTokens: history.length ? 0 : 3600,
        },
    };
}

// ===================================================================== routes

const QUARTER_RE = /^\d{4}-Q[1-4]$/;
const badQuarter = (v: string) => respond({ error: `Invalid quarter "${v}". Expected YYYY-QN, e.g. ${currentQuarter().key}.` }, 400);

export const REPORTS_ROUTES: Route[] = [
    { method: 'GET', path: '/api/board/dashboard', handler: ctx => boardDashboard(quarterParam(ctx.query)) },

    {
        method: 'GET',
        path: '/api/quarterly-report/:quarter/pdf',
        handler: ctx => {
            const key = ctx.params['quarter'];
            if (!QUARTER_RE.test(key)) {
                return badQuarter(key);
            }
            const { blob, filename } = ceoReportPdf(quarter(key));
            return respond(blob, 200, { 'Content-Type': 'application/pdf', 'Content-Disposition': `attachment; filename="${filename}"` });
        },
    },
    {
        method: 'GET',
        path: '/api/quarterly-report/:quarter',
        handler: ctx => (QUARTER_RE.test(ctx.params['quarter']) ? ceoReport(quarter(ctx.params['quarter'])) : badQuarter(ctx.params['quarter'])),
    },
    {
        method: 'POST',
        path: '/api/quarterly-report/:quarter',
        handler: ctx => (QUARTER_RE.test(ctx.params['quarter']) ? saveCeoReport(quarter(ctx.params['quarter']), ctx.body) : badQuarter(ctx.params['quarter'])),
    },

    { method: 'POST', path: '/api/chat/message', handler: chatMessage },
];
